/*
 * <heritage-cart-confirm>
 *
 * Heritage cart-confirmation modal — opens optimistically the moment a
 * visitor clicks Add to Cart, then fades in the live subtotal + cart
 * count once the network round-trip resolves.
 *
 * Two-phase event contract:
 *
 *   1. `mdj:cart-add-pending` — fired immediately on click (no network
 *      wait). The modal opens with the product preview (image / era /
 *      name / sku — known client-side) and an "Adding to cart…" status
 *      in the subtotal slot; the cart count is hidden.
 *
 *   2. `mdj:cart-add-success` — fired after /cart/add + /cart resolve.
 *      The modal swaps the "Adding to cart…" status for the real
 *      subtotal and the live cart count, with a short opacity fade.
 *
 *   3. `mdj:cart-add-error`   — fired if /cart/add fails. The modal
 *      closes; the form falls back to a native submit and the page
 *      reloads.
 *
 * Event detail contract (pending omits `cart`):
 *   {
 *     image: string, era: string, name: string, sku: string,
 *     qty:   number,
 *     cart:  object | null  // Shopify /cart.js response on success
 *   }
 *
 * Customer state (partner vs. gated) is read once server-side and
 * embedded as `data-is-partner` on this element. The component reads
 * that attribute to decide whether to render the live cart total or
 * the gated copy.
 */

class HeritageCartConfirm extends HTMLElement {
  constructor() {
    super();
    this.handleClose = this.handleClose.bind(this);
    this.handleKeydown = this.handleKeydown.bind(this);
    this.handleCartAddPending = this.handleCartAddPending.bind(this);
    this.handleCartAdd = this.handleCartAdd.bind(this);
    this.handleCartAddError = this.handleCartAddError.bind(this);
  }

  connectedCallback() {
    if (this._initialized) return;

    // Portal to document.body so the fixed-position overlay escapes
    // the parent .shopify-section's stacking context (z-index: 2 in
    // base.css). The header group sits at z-index: 100 — without
    // portaling, the modal renders under the header. Same pattern as
    // <heritage-gallery-lightbox>, <heritage-memo-modal>, and
    // <heritage-reserve-modal>.
    if (document.body && this.parentNode !== document.body) {
      document.body.appendChild(this);
      return;
    }

    this._initialized = true;

    this.imageEl    = this.querySelector('[data-cart-confirm-image]');
    this.eraEl      = this.querySelector('[data-cart-confirm-era]');
    this.nameEl     = this.querySelector('[data-cart-confirm-name]');
    this.metaEl     = this.querySelector('[data-cart-confirm-meta]');
    this.subtotalEl = this.querySelector('[data-cart-confirm-subtotal]');
    this.countEl    = this.querySelector('[data-cart-confirm-count]');
    this.overlayEl  = this.querySelector('[data-cart-confirm-overlay]');
    this.closeEls   = this.querySelectorAll('[data-cart-confirm-close]');

    this.isPartner = this.dataset.isPartner === 'true';
    this.gatedText = this.dataset.gatedSubtotalText || 'Trade pricing on sign-in';
    this.pendingText = this.dataset.pendingSubtotalText || 'Adding to cart…';
    this.errorText = this.dataset.errorText || "Couldn't add this piece — please refresh and try again, or call MDJ.";
    this.moneyPrefix = this.dataset.moneyPrefix || '$';
    this.singularTpl = this.dataset.itemCountSingular || '%d item';
    this.pluralTpl   = this.dataset.itemCountPlural   || '%d items';

    this.closeEls.forEach((btn) => btn.addEventListener('click', this.handleClose));
    if (this.overlayEl) this.overlayEl.addEventListener('click', this.handleClose);
    document.addEventListener('keydown', this.handleKeydown);
    document.addEventListener('mdj:cart-add-pending', this.handleCartAddPending);
    document.addEventListener('mdj:cart-add-success', this.handleCartAdd);
    document.addEventListener('mdj:cart-add-error', this.handleCartAddError);
  }

  disconnectedCallback() {
    document.removeEventListener('keydown', this.handleKeydown);
    document.removeEventListener('mdj:cart-add-pending', this.handleCartAddPending);
    document.removeEventListener('mdj:cart-add-success', this.handleCartAdd);
    document.removeEventListener('mdj:cart-add-error', this.handleCartAddError);
  }

  handleCartAddPending(event) {
    const detail = event && event.detail ? event.detail : {};
    this.show(detail, { pending: true });
  }

  handleCartAdd(event) {
    const detail = event && event.detail ? event.detail : {};
    this.show(detail, { pending: false });
  }

  handleCartAddError() {
    // The add failed (network, 404 on product JSON, sold-out variant,
    // etc). Keep the modal open and switch to an error state so the
    // visitor sees feedback. Previously we auto-closed on error which
    // worked for the PDP form (it falls back to native submit + page
    // navigation, so the modal vanishing was masked), but the vault
    // Add-to-Cart flow has no native fallback — the modal would flash
    // open then disappear with no explanation.
    //
    // PDP flow still navigates away after the form.submit() in the
    // catch branch, so the error state shows briefly before reload.
    // Vault flow: error sticks until the visitor dismisses it.
    if (this.dataset.open !== 'true') return;
    this.dataset.state = 'error';
    if (this.subtotalEl) {
      this.subtotalEl.textContent = this.errorText;
    }
    if (this.countEl) this.countEl.textContent = '';
  }

  handleClose(event) {
    if (event) event.preventDefault();
    this.close();
  }

  handleKeydown(event) {
    if (event.key === 'Escape' && this.dataset.open === 'true') this.close();
  }

  show(detail, opts) {
    const pending = !!(opts && opts.pending);
    const {
      image = '',
      era = '',
      name = '',
      sku = '',
      qty = 1,
      cart = null,
      mode = 'cart'
    } = detail || {};

    // Mode toggle — 'memo' came from the PDP "Request on Memo" button
    // (heritage-pdp-form forwards `event.submitter.dataset.addMode`),
    // anything else gets the default Add-to-Cart copy block.
    this.dataset.mode = mode === 'memo' ? 'memo' : 'default';

    if (this.imageEl) {
      this.imageEl.style.backgroundImage = image ? `url('${image}')` : '';
    }
    if (this.eraEl) this.eraEl.textContent = era || '';
    if (this.nameEl) this.nameEl.textContent = name || '';
    if (this.metaEl) {
      // Strip the configured display prefix (idempotent). Most
      // entrypoints already pass a stripped SKU (PDP attrs and new
      // vault entries flow through Liquid), but vault items saved
      // before the strip was in place still carry the raw "40XXXXX".
      const cleanSku = window.MDJFormatSku ? window.MDJFormatSku(sku) : sku;
      const parts = [];
      if (cleanSku) parts.push(`No. ${cleanSku}`);
      if (qty) parts.push(`Qty ${qty}`);
      this.metaEl.textContent = parts.join(' · ');
    }

    if (this.subtotalEl) {
      if (pending) {
        this.subtotalEl.textContent = this.pendingText;
      } else if (this.isPartner && cart && typeof cart.total_price === 'number') {
        this.subtotalEl.textContent = this.formatMoney(cart.total_price);
      } else {
        this.subtotalEl.textContent = this.gatedText;
      }
    }

    if (this.countEl) {
      if (pending) {
        this.countEl.textContent = '';
      } else {
        const count = cart && typeof cart.item_count === 'number' ? cart.item_count : qty;
        const tpl = count === 1 ? this.singularTpl : this.pluralTpl;
        this.countEl.textContent = tpl.replace('%d', count);
      }
    }

    this.dataset.state = pending ? 'pending' : 'ready';
    this.dataset.open = 'true';
    this.setAttribute('aria-hidden', 'false');
    document.documentElement.classList.add('heritage-overlay-open');
  }

  close() {
    this.dataset.open = 'false';
    this.setAttribute('aria-hidden', 'true');
    document.documentElement.classList.remove('heritage-overlay-open');
  }

  formatMoney(cents) {
    const amount = (cents / 100).toFixed(2);
    return `${this.moneyPrefix}${amount}`;
  }
}

if (!customElements.get('heritage-cart-confirm')) {
  customElements.define('heritage-cart-confirm', HeritageCartConfirm);
}
